import { useCallback, useEffect, useRef, useState } from "react";
import { NextSeo } from "next-seo";
import { extractFirstQueryParam } from "domains/commons/misc";
import ModelTrain from "domains/models/components/ModelTrain";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import {
  PostModelsApiResponse,
  useGetModelsByModelIdQuery,
} from "infra/api/generated/api";

import { skipToken } from "@reduxjs/toolkit/query";

export default function ModelsNew() {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const [modelKey, setModelKey] = useState<number>(new Date().getTime());

  const id = extractFirstQueryParam(router.query.id);
  const prevIdRef = useRef<string | undefined>(id);

  const handleTrain = useCallback(async () => {
    if (!id) return;
    await router.replace({
      pathname: "/models/[id]",
      query: {
        id,
      },
    });
  }, [router, id]);

  const handleCreateModel = useCallback(
    async (id: PostModelsApiResponse["model"]["id"]) => {
      await router.replace({
        pathname: "/models/new/[id]",
        query: {
          id,
        },
      });
    },
    [router]
  );

  const handleAlreadyTrained = useCallback(() => {
    if (!id) return;
    void router.replace({
      pathname: "/models/[id]",
      query: {
        id,
      },
    });
  }, [router, id]);

  useEffect(() => {
    if (!id && prevIdRef.current) {
      setModelKey(new Date().getTime());
    }
    prevIdRef.current = id;
  }, [id, setModelKey]);

  const {
    isLoading: isModelLoading,
    data: modelData,
    refetch: refetchModel,
  } = useGetModelsByModelIdQuery(
    id
      ? {
          modelId: id,
          projectId: selectedProject.id,
        }
      : skipToken
  );

  useEffect(() => {
    if (modelData?.model && modelData.model.ownerId !== selectedProject.id) {
      void router.push({
        pathname: "/assets",
        query: { tab: "model" },
      });
    }
  }, [modelData, router, selectedProject.id]);

  return (
    <>
      <NextSeo title="Create Model" />

      <ModelTrain
        key={`${modelKey}`}
        model={modelData?.model}
        isLoading={isModelLoading}
        refetchModel={refetchModel}
        onTrain={handleTrain}
        onCreateModel={handleCreateModel}
        onAlreadyTrained={handleAlreadyTrained}
      />
    </>
  );
}
